package com.fantasytenis.shared.data.contests.api

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.core.network.GenericErrorResponse
import com.fantasytenis.shared.core.network.lenientJson
import com.fantasytenis.shared.data.contests.dto.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*

/**
 * Contests API client.
 *
 * Handles all contest-related HTTP requests.
 */
class ContestsApi(
    private val httpClient: HttpClient,
    private val baseUrl: String
) {

    // ===== CONTESTS =====

    /**
     * Get a contest by ID.
     * GET /v2/contests/:id
     */
    suspend fun getContest(contestId: String): ApiResponse<ContestDto> {
        return try {
            val response = httpClient.get("${baseUrl}contests/$contestId")

            if (response.status.isSuccess()) {
                val wrapped = response.body<ContestApiResponse<ContestDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Contest not found", response.status.value)
                }
            } else {
                val wrapped = response.body<ContestApiResponse<ContestDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get contest", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * List contests with optional filters.
     * GET /v2/contests
     */
    suspend fun listContests(
        status: Int? = null,
        type: Int? = null,
        slateId: String? = null,
        limit: Int = 20,
        offset: Int = 0
    ): ApiResponse<List<ContestDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}contests?limit=$limit&offset=$offset")
                status?.let { append("&status=$it") }
                type?.let { append("&type=$it") }
                slateId?.let { append("&slateId=$it") }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<ContestListResponse<ContestDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to list contests", response.status.value)
                }
            } else {
                val wrapped = response.body<ContestListResponse<ContestDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to list contests", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== ENTRIES =====

    /**
     * Enter a contest.
     * POST /v2/contests/:id/enter
     */
    suspend fun enterContest(contestId: String): ApiResponse<ContestEntryDto> {
        return try {
            val response = httpClient.post("${baseUrl}contests/$contestId/enter")

            // Backend returns HTTP 200 even for errors, check body status first
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                val wrapped = lenientJson.decodeFromString<ContestApiResponse<ContestEntryDto>>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to enter contest", wrapped.status)
                }
            } else {
                ApiResponse.Error(errorCheck.error ?: "Failed to enter contest", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Cancel a contest entry.
     * DELETE /v2/contests/entries/:entryId
     */
    suspend fun cancelEntry(entryId: String): ApiResponse<ContestEntryDto> {
        return try {
            val response = httpClient.delete("${baseUrl}contests/entries/$entryId")

            // Backend returns HTTP 200 even for errors, check body status first
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                val wrapped = lenientJson.decodeFromString<ContestApiResponse<ContestEntryDto>>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to cancel entry", wrapped.status)
                }
            } else {
                ApiResponse.Error(errorCheck.error ?: "Failed to cancel entry", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get user's contest entries.
     * GET /v2/users/me/entries
     */
    suspend fun getUserEntries(contestId: String? = null): ApiResponse<List<ContestEntryDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}users/me/entries")
                contestId?.let { append("?contestId=$it") }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<ContestListResponse<ContestEntryDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to get entries", response.status.value)
                }
            } else {
                val wrapped = response.body<ContestListResponse<ContestEntryDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get entries", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== LEADERBOARD =====

    /**
     * Get contest leaderboard.
     * GET /v2/contests/:id/leaderboard
     */
    suspend fun getContestLeaderboard(
        contestId: String,
        limit: Int = 50,
        offset: Int = 0
    ): ApiResponse<List<ContestEntryDto>> {
        return try {
            val url = "${baseUrl}contests/$contestId/leaderboard?limit=$limit&offset=$offset"

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<ContestListResponse<ContestEntryDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to get leaderboard", response.status.value)
                }
            } else {
                val wrapped = response.body<ContestListResponse<ContestEntryDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get leaderboard", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== SLATES =====

    /**
     * Get a slate by ID.
     * GET /v2/slates/:id
     */
    suspend fun getSlate(slateId: String): ApiResponse<SlateDto> {
        return try {
            val response = httpClient.get("${baseUrl}slates/$slateId")

            if (response.status.isSuccess()) {
                val wrapped = response.body<ContestApiResponse<SlateDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Slate not found", response.status.value)
                }
            } else {
                val wrapped = response.body<ContestApiResponse<SlateDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get slate", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get players for a slate (for lineup building).
     * GET /v2/slates/:id/players
     */
    suspend fun getSlatePlayers(slateId: String): ApiResponse<List<SlatePlayerDto>> {
        return try {
            val response = httpClient.get("${baseUrl}slates/$slateId/players")

            if (response.status.isSuccess()) {
                val wrapped = response.body<ContestListResponse<SlatePlayerDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to get slate players", response.status.value)
                }
            } else {
                val wrapped = response.body<ContestListResponse<SlatePlayerDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get slate players", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== ADMIN: LOCK OVERRIDE =====

    /**
     * Set lock override for a contest (SuperAdmin only).
     * PUT /v2/admin/contests/:id/lock-override
     */
    suspend fun setLockOverride(
        contestId: String,
        request: SetContestLockOverrideRequest
    ): ApiResponse<ContestDto> {
        return try {
            val response = httpClient.put("${baseUrl}admin/contests/$contestId/lock-override") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<SetContestLockOverrideResponse>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to set lock override", response.status.value)
                }
            } else {
                val wrapped = response.body<SetContestLockOverrideResponse>()
                ApiResponse.Error(wrapped.error ?: "Failed to set lock override", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== ADMIN: DELETE ENTRY =====

    /**
     * Admin delete a contest entry (SuperAdmin only).
     * DELETE /v2/admin/contests/entries/:entryId
     */
    suspend fun adminDeleteEntry(
        entryId: String,
        request: AdminDeleteEntryRequest
    ): ApiResponse<AdminDeleteEntryResponse> {
        return try {
            val response = httpClient.delete("${baseUrl}admin/contests/entries/$entryId") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<AdminDeleteEntryResponse>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to delete entry", response.status.value)
                }
            } else {
                val wrapped = response.body<AdminDeleteEntryResponse>()
                ApiResponse.Error(wrapped.error ?: "Failed to delete entry", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }
}
